import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { CreditCard, ArrowUp, ArrowDown, ShoppingBag, Coffee, Utensils, Plus, Lock, Lightbulb, TrendingUp, Gift } from "lucide-react";
import { useTranslation } from "react-i18next";

interface DigitalWalletWidgetProps {
    color?: string;
}

const DigitalWalletWidget: React.FC<DigitalWalletWidgetProps> = ({ color = "from-emerald-400 to-green-500" }) => {
    const { t, i18n } = useTranslation("nonHealth");
    const [balance] = useState(125000);

    const transactions = useMemo(() => [
        { label: t("widgets.digitalWallet.recentTransactions.items.schoolBuffet"), amount: -12000, icon: <Coffee className="w-3.5 h-3.5" />, color: "text-rose-500", time: "10:30" },
        { label: t("widgets.digitalWallet.recentTransactions.items.dadCharge"), amount: +50000, icon: <ArrowDown className="w-3.5 h-3.5" />, color: "text-green-500", time: "18:00" },
        { label: t("widgets.digitalWallet.recentTransactions.items.lunch"), amount: -18000, icon: <Utensils className="w-3.5 h-3.5" />, color: "text-rose-500", time: "12:45" },
    ], [t]);

    const formatAmount = (amount: number) => {
        const absAmount = Math.abs(amount);
        const formatted = new Intl.NumberFormat(i18n.language).format(absAmount);
        return `${amount > 0 ? "+" : amount < 0 ? "-" : ""}${formatted} ${t("widgets.digitalWallet.currency")}`;
    };

    return (
        <motion.div
            key="digitalWallet-widget"
            className="relative w-full max-w-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5, type: "spring", stiffness: 100 }}
        >
            <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-5 shadow-xl border border-emerald-200 relative overflow-hidden">
                {/* افکت‌های تزئینی پس‌زمینه */}
                <motion.div
                    className="absolute -left-10 top-10 w-40 h-40 bg-gradient-to-br from-emerald-400/15 to-green-500/10 rounded-full blur-2xl"
                    animate={{ scale: [1, 1.3, 1], opacity: [0.4, 0.7, 0.4] }}
                    transition={{ duration: 8, repeat: Infinity }}
                />
                <motion.div
                    className="absolute -right-10 -bottom-10 w-36 h-36 bg-gradient-to-tr from-green-400/15 to-emerald-500/10 rounded-full blur-2xl"
                    animate={{ scale: [1.2, 1, 1.2], opacity: [0.4, 0.7, 0.4] }}
                    transition={{ duration: 8, repeat: Infinity, delay: 1 }}
                />

                {/* کارت اصلی موجودی */}
                <motion.div
                    className={`bg-gradient-to-r ${color} rounded-2xl p-4 mb-4 shadow-lg relative overflow-hidden`}
                    whileHover={{ scale: 1.02, boxShadow: "0 12px 30px rgba(16, 185, 129, 0.3)" }}
                >
                    {/* الگوی پس‌زمینه کارت */}
                    <div className="absolute inset-0 overflow-hidden">
                        {[...Array(3)].map((_, i) => (
                            <motion.div
                                key={i}
                                className="absolute w-32 h-32 rounded-full border border-white/20"
                                style={{ top: `${-20 + i * 20}%`, right: `${-10 + i * 15}%` }}
                                animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
                                transition={{ duration: 3 + i, repeat: Infinity }}
                            />
                        ))}
                    </div>

                    <div className="relative z-10">
                        <div className="flex justify-between items-start mb-3">
                            <div>
                                <p className="text-white/80 text-xs mb-1">{t("widgets.digitalWallet.balanceTitle")}</p>
                                <motion.p className="text-white font-bold text-2xl" animate={{ scale: [1, 1.02, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                                    {new Intl.NumberFormat(i18n.language).format(balance)} {t("widgets.digitalWallet.currency")}
                                </motion.p>
                            </div>
                            <motion.div
                                className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center"
                                whileHover={{ scale: 1.1, backgroundColor: "rgba(255,255,255,0.3)" }}
                                animate={{ rotate: [0, 5, -5, 0] }}
                                transition={{ duration: 4, repeat: Infinity }}
                            >
                                <CreditCard className="w-5 h-5 text-white" />
                            </motion.div>
                        </div>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                                <motion.div className="w-2 h-2 rounded-full bg-white" animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1.5, repeat: Infinity }} />
                                <p className="text-white/80 text-xs">{t("widgets.digitalWallet.cardTitle")}</p>
                            </div>
                            <motion.div
                                className="flex items-center gap-1 bg-white/20 px-2 py-1 rounded-full text-xs text-white"
                                animate={{ scale: [1, 1.03, 1] }}
                                transition={{ duration: 2, repeat: Infinity }}
                            >
                                <Lock className="w-3 h-3" />
                                {t("widgets.digitalWallet.dailyLimit", { amount: new Intl.NumberFormat(i18n.language).format(50000) })}
                            </motion.div>
                        </div>
                    </div>
                </motion.div>

                {/* دکمه‌های اکشن */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                    {[
                        { label: t("widgets.digitalWallet.actions.charge"), icon: <ArrowDown className="w-5 h-5" />, color: "from-green-500 to-emerald-600" },
                        { label: t("widgets.digitalWallet.actions.transaction"), icon: <TrendingUp className="w-5 h-5" />, color: "from-blue-500 to-sky-600" },
                        { label: t("widgets.digitalWallet.actions.gift"), icon: <Gift className="w-5 h-5" />, color: "from-rose-500 to-pink-600" },
                    ].map((action, idx) => (
                        <motion.button
                            key={idx}
                            className={`flex flex-col items-center gap-1 py-2.5 px-2 rounded-xl bg-gradient-to-b ${action.color} text-white shadow-sm`}
                            whileHover={{ scale: 1.05, y: -2, boxShadow: "0 8px 16px rgba(0,0,0,0.15)" }}
                            whileTap={{ scale: 0.95 }}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 * idx }}
                        >
                            {action.icon}
                            <span className="text-xs font-medium">{action.label}</span>
                        </motion.button>
                    ))}
                </div>

                {/* تراکنش‌های اخیر */}
                <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                        <p className="text-gray-700 text-xs font-semibold">{t("widgets.digitalWallet.recentTransactions.title")}</p>
                        <motion.span className="text-xs text-emerald-600 font-medium cursor-pointer" whileHover={{ scale: 1.05 }}>
                            {t("widgets.digitalWallet.recentTransactions.viewAll")}
                        </motion.span>
                    </div>
                    <div className="space-y-2">
                        {transactions.map((tx, idx) => (
                            <motion.div
                                key={idx}
                                className="flex items-center justify-between bg-gray-50 px-3 py-2.5 rounded-xl border border-gray-100 shadow-sm"
                                whileHover={{ y: -1, boxShadow: "0 4px 12px rgba(0,0,0,0.08)", backgroundColor: "white" }}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.1 + idx * 0.1 }}
                            >
                                <div className="flex items-center gap-2.5">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${tx.amount > 0 ? "bg-green-100" : "bg-rose-100"}`}>
                                        <span className={tx.color}>{tx.icon}</span>
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-gray-700">{tx.label}</p>
                                        <p className="text-xs text-gray-400">{tx.time}</p>
                                    </div>
                                </div>
                                <motion.span
                                    className={`text-xs font-bold ${tx.amount > 0 ? "text-green-600" : "text-rose-600"}`}
                                    animate={{ scale: [1, 1.03, 1] }}
                                    transition={{ duration: 2, repeat: Infinity, delay: idx * 0.5 }}
                                >
                                    {formatAmount(tx.amount)}
                                </motion.span>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* دکمه شارژ */}
                <motion.button
                    className={`w-full bg-gradient-to-r ${color} text-white py-2.5 rounded-xl text-sm font-semibold shadow-md relative overflow-hidden`}
                    whileHover={{ scale: 1.02, y: -2, boxShadow: "0 8px 20px rgba(16, 185, 129, 0.3)" }}
                    whileTap={{ scale: 0.98 }}
                >
                    <motion.span
                        className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                        initial={{ x: "-100%" }}
                        animate={{ x: "100%" }}
                        transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
                    />
                    <span className="relative z-10 flex items-center justify-center gap-2">
                        <Plus className="w-4 h-4" />
                        {t("widgets.digitalWallet.chargeButton")}
                    </span>
                </motion.button>

                {/* پیام راهنما */}
                <motion.div
                    className="mt-3 bg-gradient-to-r from-blue-50 to-sky-50 border border-blue-200 text-blue-700 text-xs p-2 rounded-lg flex items-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.7 }}
                >
                    <motion.span
                        className="ml-2 bg-blue-100 w-6 h-6 rounded-full flex items-center justify-center shrink-0"
                        animate={{ rotate: [0, -10, 10, 0] }}
                        transition={{ duration: 3, repeat: Infinity }}
                    >
                        <Lightbulb className="w-3.5 h-3.5" />
                    </motion.span>
                    <span>{t("widgets.digitalWallet.tip")}</span>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default DigitalWalletWidget;
